import React, { createContext, useContext, useState, useCallback } from 'react';

import { useToast } from './toast';

interface ProductsType {
  id: number;
  unidade_medida_id: number;
  descricao: string;
  codigo: string;
  gtin: string;
  tipo: number;
  preco: number;
  observacao: string;
  imagem: string;
  estoque_minimo: number;
  estoque_atual: number;
  grupo_id: number;
  // Fiscal NFC-e (Story 1-4 / T1.38)
  categoria_fiscal_id?: number | null;
  ncm_override?: string | null;
  cfop_override?: string | null;
  cest_override?: string | null;
  origem_override?: string | null;
}

interface ITurnoType {
  inicio: number;
  fim: number;
}

interface DisponibilidadeType {
  dias: {
    domingo: boolean;
    segunda: boolean;
    terca: boolean;
    quarta: boolean;
    quinta: boolean;
    sexta: boolean;
    sabado: boolean;
  };
  turnos: ITurnoType[];
}

interface QtdTurnos {
  index: number;
}

interface TurnoType {
  inicio: number;
  fim: number;
}

interface EditProdutoContextData {
  produto: ProductsType;
  disponibilidade: DisponibilidadeType;
  editDrawerOpen: boolean;
  qtdTurnos: QtdTurnos[];
  turnos: TurnoType[];
  sempreDisponivel: boolean;

  handleSetProduto(data: ProductsType): void;
  handleSetDisponibilidade(data: DisponibilidadeType): void;
  handleSetEditDrawerOpen(value: boolean): void;
  handleSetQtdTurno(operacao: string, item_index?: number): void;
  handleSetSempreDisponivel(value: boolean): void;
  handleSetTurnos(data: TurnoType[]): void;
  handleSetInitialQtdTurnos(data: QtdTurnos[]): void;
  cleanData(): void;
}

const EditProdutoContext = createContext<EditProdutoContextData>(
  {} as EditProdutoContextData,
);

const EditProdutoProvider: React.FC = ({ children }) => {
  const [produto, setProduto] = useState<ProductsType>(
    () => { return {} as ProductsType }
  );
  const [disponibilidade, setDisponibilidade] = useState<DisponibilidadeType>(
    () => { return {} as DisponibilidadeType }
  );
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [sempreDisponivel, setSempreDisponivel] = useState(true);
  const [qtdTurnos, setQtdTurnos] = useState<QtdTurnos[]>([]);
  const [turnos, setTurnos] = useState<TurnoType[]>([]);

  const { addToast } = useToast();

  const handleSetProduto = useCallback((data: ProductsType) => {
    setProduto({ ...data });
  }, []);

  const handleSetDisponibilidade = useCallback((data: DisponibilidadeType) => {
    setDisponibilidade({ ...data });
  }, []);

  const handleSetEditDrawerOpen = useCallback((value: boolean) => {
    setEditDrawerOpen(value);
  }, []);

  const handleSetSempreDisponivel = useCallback((value: boolean) => {
    setSempreDisponivel(value);
  }, []);

  const handleSetTurnos = useCallback((data: TurnoType[]) => {
    setTurnos(data);
  }, []);

  const cleanData = useCallback(() => {
    setProduto({} as ProductsType);
    setDisponibilidade({} as DisponibilidadeType);
  }, []);

  const handleSetInitialQtdTurnos = useCallback((data: QtdTurnos[]) => {
    setQtdTurnos([...data]);
  }, []);

  const handleSetQtdTurno = useCallback(
    (operacao: string, item_index?: number) => {
      const qtdAtual = [...qtdTurnos];
      const turnosAtuais = [...turnos];

      if (operacao === 'add') {
        if (qtdTurnos.length == 0) {
          setQtdTurnos([{ index: 0 }]);
          setTurnos([{ inicio: 0, fim: 0 }]);
          return;
        }

        qtdAtual.push({ index: qtdAtual[qtdTurnos.length - 1].index + 1 });
        turnosAtuais.push({ inicio: 0, fim: 0 });

        setQtdTurnos([...qtdAtual]);
        setTurnos([...turnosAtuais]);
      } else if (operacao === 'remove') {
        const qtdTurnosStoreds: QtdTurnos[] = qtdTurnos;
        const turnosStored = [...turnos];

        if (item_index !== undefined) {
          turnosStored.splice(item_index, 1);
        }
        qtdTurnosStoreds.pop();

        setQtdTurnos([...qtdTurnosStoreds]);
        setTurnos([...turnosStored]);
      }
    },
    [qtdTurnos, addToast, turnos],
  );

  return (
    <EditProdutoContext.Provider
      value={{
        produto,
        disponibilidade,
        editDrawerOpen,
        qtdTurnos,
        turnos,
        sempreDisponivel,
        handleSetProduto,
        handleSetDisponibilidade,
        handleSetEditDrawerOpen,
        handleSetQtdTurno,
        handleSetSempreDisponivel,
        handleSetTurnos,
        handleSetInitialQtdTurnos,
        cleanData,
      }}
    >
      {children}
    </EditProdutoContext.Provider>
  )
}

function useEditProduto(): EditProdutoContextData {
  const context = useContext(EditProdutoContext);

  if (!context)
    throw new Error(
      'useEditProduto deve ser utilizado junto do EditProdutoProvider',
    );

  return context;
}

export { EditProdutoProvider, useEditProduto };
